<template>
  <div class="rolling-items">
    <div v-if="loading" class="text-center py-1">
      <b-spinner small />
      <span class="ml-50">Carregando a composição da rolagem...</span>
    </div>
    <div v-else-if="error" class="text-danger py-1">
      Não foi possível carregar a composição da rolagem.
    </div>
    <template v-else-if="details">
      <!--
        Rolagem anterior a mar/2026 (ou rolagem por produto legada): o fechamento ainda não
        gravava os itens. O card proíbe reconstruir por presunção, então só se avisa.
      -->
      <div v-if="!summary.has_history" class="text-muted py-1">
        Esta rolagem foi gerada antes do detalhamento por item e não tem a composição registrada.
      </div>
      <template v-else>
        <div
          v-for="item in details.items"
          :key="item.history_id"
          class="rolling-item py-50"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div class="pr-1">
              <div>
                <b>{{ item.description || "Sem descrição" }}</b>
                <b-badge
                  v-if="item.origin_competence"
                  variant="light-secondary"
                  class="ml-50"
                  title="Competência de onde o item foi transferido"
                >
                  Rolagem de {{ item.origin_competence }}
                </b-badge>
                <b-badge v-if="item.is_deleted" variant="light-warning" class="ml-50">
                  Excluído após a rolagem
                </b-badge>
              </div>
              <small class="text-muted d-block">
                {{ kindLabel(item.kind) }} #{{ item.source_id }}
                <template v-if="item.installment"> · Parcela {{ item.installment }}</template>
                <template v-if="item.reason"> · {{ item.reason }}</template>
                <template v-if="item.reference_competence">
                  · Competência de referência {{ item.reference_competence }}
                </template>
              </small>
              <small
                v-for="contract in item.contracts"
                :key="`${item.history_id}-${contract.id}`"
                class="text-muted d-block"
              >
                Contrato #{{ contract.id }}
                <template v-if="contract.customer_name"> · {{ contract.customer_name }}</template>
                <template v-if="contract.financial_solution_name">
                  · {{ contract.financial_solution_name }}
                </template>
              </small>
              <small v-if="item.paying_company_name || item.batch_reference" class="text-muted d-block">
                <template v-if="item.paying_company_name">{{ item.paying_company_name }}</template>
                <template v-if="item.paying_company_name && item.batch_reference"> · </template>
                <template v-if="item.batch_reference">Lote {{ item.batch_reference }}</template>
              </small>
              <small v-if="item.added_by_name || item.approved_by_name" class="text-muted d-block">
                <template v-if="item.added_by_name">
                  Lançado por {{ item.added_by_name }} em {{ item.created_at | onlyDate }}
                </template>
                <template v-if="item.added_by_name && item.approved_by_name"> · </template>
                <template v-if="item.approved_by_name">
                  Aprovado por {{ item.approved_by_name }} em {{ item.approved_at | onlyDate }}
                </template>
              </small>
              <small v-if="item.comment" class="text-muted d-block">{{ item.comment }}</small>
              <b-link
                v-if="item.has_rolling_details"
                class="small"
                @click="toggleChain(item.source_id)"
              >
                {{ expandedChains.includes(item.source_id) ? "Ocultar" : "Ver" }}
                a composição da rolagem anterior
              </b-link>
            </div>
            <span
              class="text-nowrap"
              :class="item.amount < 0 ? 'text-danger' : 'text-success'"
            >
              {{ item.amount | toCurrency }}
            </span>
          </div>
          <!-- A cadeia: ajuste que já tinha rolado antes continua rastreável até a origem. -->
          <div
            v-if="item.has_rolling_details && expandedChains.includes(item.source_id)"
            class="rolling-chain pl-1 mt-50"
          >
            <rolling-items-details :rolling-id="item.source_id" :own="own" nested />
          </div>
        </div>

        <!-- Totalizador: complementa as linhas, nunca as substitui. -->
        <div class="rolling-summary pt-50">
          <div class="d-flex justify-content-between">
            <span>Total dos itens ({{ summary.items_count }})</span>
            <span class="text-nowrap">{{ summary.items_amount | toCurrency }}</span>
          </div>
          <div
            v-if="summary.difference_kind !== 'none'"
            class="d-flex justify-content-between text-muted"
            :title="differenceTitle"
          >
            <span>
              {{ summary.difference_kind === "rounding" ? "Arredondamento" : "Diferença não detalhada" }}
              <feather-icon icon="InfoIcon" size="12" />
            </span>
            <span class="text-nowrap">{{ summary.difference | toCurrency }}</span>
          </div>
          <div class="d-flex justify-content-between">
            <b>Valor da rolagem</b>
            <b class="text-nowrap">{{ summary.rolling_amount | toCurrency }}</b>
          </div>
        </div>
      </template>

      <div v-if="!nested" class="d-flex flex-wrap justify-content-end mt-1">
        <slot name="actions" :details="details" />
        <b-button
          v-if="summary.has_history"
          variant="outline-primary"
          size="sm"
          :disabled="exporting"
          @click="exportItems"
        >
          <b-spinner v-if="exporting" small />
          <feather-icon v-else icon="DownloadIcon" class="mr-50" />
          Baixar planilha
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
import { BBadge, BButton, BLink, BSpinner } from "bootstrap-vue";
import { mapActions } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import * as types from "../store/types";

/**
 * A composição da linha "Rolagem": cada comissão e ajuste da competência anterior que foi
 * transferido, com descrição, valor e sinal originais.
 *
 * Usado na tela de lançamentos do PN (`own`, rota restrita ao próprio consultor) e no detalhe do
 * ajuste do Financeiro. Recursivo: um item que é a rolagem anterior abre a própria composição.
 */
export default {
  name: "RollingItemsDetails",
  components: {
    BBadge,
    BButton,
    BLink,
    BSpinner,
  },
  props: {
    rollingId: {
      type: Number,
      required: true,
    },
    own: {
      type: Boolean,
      default: false,
    },
    nested: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      loading: false,
      exporting: false,
      error: false,
      details: null,
      expandedChains: [],
    };
  },
  computed: {
    summary() {
      return this.details?.summary || {};
    },
    differenceTitle() {
      return this.summary.difference_kind === "rounding"
        ? "Centavos de arredondamento: cada item é registrado com duas casas decimais."
        : "Parte do valor da rolagem que não corresponde a nenhum item registrado da competência anterior.";
    },
  },
  watch: {
    rollingId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    ...mapActions({
      getRollingItems: types.GET_ROLLING_ITEMS,
      exportRollingItems: types.EXPORT_ROLLING_ITEMS,
    }),
    load() {
      this.loading = true;
      this.error = false;
      this.expandedChains = [];
      this.getRollingItems({ id: this.rollingId, own: this.own })
        .then((response) => {
          this.details = response.data;
        })
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    kindLabel(kind) {
      return (
        {
          commission: "Comissão",
          adjustment: "Ajuste",
          rolling: "Rolagem anterior",
        }[kind] || kind
      );
    },
    toggleChain(id) {
      this.expandedChains = this.expandedChains.includes(id)
        ? this.expandedChains.filter((chainId) => chainId !== id)
        : [...this.expandedChains, id];
    },
    exportItems() {
      this.exporting = true;
      this.exportRollingItems({ id: this.rollingId, own: this.own })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao baixar a planilha da rolagem. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.exporting = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.rolling-item + .rolling-item {
  border-top: 1px dashed rgba(34, 41, 47, 0.125);
}
.rolling-summary {
  border-top: 1px solid rgba(34, 41, 47, 0.125);
}
.rolling-chain {
  border-left: 2px solid rgba(34, 41, 47, 0.125);
}
</style>
